import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, GitCompareArrows, Plus, X } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import AIAdvisor from "@/components/AIAdvisor";
import { scoreColor } from "@/components/ScoreGauge";
import api from "@/lib/api";

const g = (r, path) => path.split(".").reduce((o, k) => (o ? o[k] : undefined), r);

const METRICS = [
  { label: "Readiness Score", get: (r) => g(r, "scores.integration"), score: true },
  { label: "Frontend Score", get: (r) => g(r, "scores.frontend"), score: true },
  { label: "Mobile Score", get: (r) => g(r, "scores.mobile"), score: true },
  { label: "Security Score", get: (r) => g(r, "scores.security"), score: true },
  { label: "SEO Score", get: (r) => g(r, "scores.seo"), score: true },
  { label: "Compliance Score", get: (r) => g(r, "scores.compliance"), score: true },
  { label: "Framework", get: (r) => g(r, "frontend.framework") },
  { label: "React version", get: (r) => g(r, "frontend.reactVersion") },
  { label: "Bundler", get: (r) => g(r, "frontend.bundler") },
  { label: "jQuery", get: (r) => (g(r, "frontend.usesjQuery") ? "Yes" : "No") },
  { label: "Responsive", get: (r) => (g(r, "mobile.isResponsive") ? "Yes" : "No") },
  { label: "IP", get: (r) => g(r, "ip") },
  { label: "Host / Org", get: (r) => g(r, "ipInfo.org") || g(r, "ipInfo.isp") },
  { label: "CDN", get: (r) => g(r, "cdn") },
  { label: "SSL valid", get: (r) => (g(r, "ssl.valid") ? "Yes" : "No") },
  { label: "HSTS", get: (r) => (g(r, "security.hsts") ? "Enabled" : "Missing") },
  { label: "CSP", get: (r) => (g(r, "security.csp") ? "Enabled" : "Missing") },
  { label: "Registrar", get: (r) => g(r, "whois.registrar") },
];

export default function Compare() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.get("/domains");
      setAvailable(list.data.domains || []);
      const wanted = params.get("domains");
      if (wanted) {
        const res = await api.get(`/compare?domains=${wanted}`);
        setResults((res.data.results || []).filter(Boolean));
      }
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  const addDomain = async (domain) => {
    if (results.find((r) => r.domain === domain)) return;
    const res = await api.get(`/compare?domains=${encodeURIComponent(domain)}`);
    const r = (res.data.results || [])[0];
    if (r) setResults((p) => [...p, r]);
  };

  const removeDomain = (domain) => setResults((p) => p.filter((r) => r.domain !== domain));

  const notAdded = available.filter((a) => !results.find((r) => r.domain === a.domain));

  return (
    <Layout>
      <div className="flex items-center gap-2 text-[#4da3ff] mb-2">
        <GitCompareArrows size={16} />
        <span className="text-xs font-bold uppercase tracking-[0.2em]">Comparison Matrix</span>
      </div>
      <h1 className="font-heading text-3xl font-bold tracking-tighter text-white mb-2">Compare Domains</h1>
      <p className="text-zinc-400 mb-6">Side-by-side technical comparison to plan a unified stack.</p>

      {/* add domains */}
      <div className="flex flex-wrap gap-2 mb-6">
        {notAdded.map((a) => (
          <button
            key={a.domain}
            data-testid={`add-compare-${a.domain}`}
            onClick={() => addDomain(a.domain)}
            className="flex items-center gap-1.5 bg-white/5 border border-white/10 text-zinc-300 hover:border-[#007AFF] hover:text-white rounded-full px-3 py-1.5 text-sm font-mono transition-colors"
          >
            <Plus size={13} /> {a.domain}
          </button>
        ))}
        {notAdded.length === 0 && available.length === 0 && (
          <span className="text-zinc-600 text-sm">No domains yet — scan some on the dashboard.</span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#007AFF]" /></div>
      ) : results.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-xl text-zinc-500">
          Add at least one domain above to compare.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-white/10 mb-6">
            <table className="w-full border-collapse" data-testid="comparison-table">
              <thead>
                <tr>
                  <th className="sticky left-0 z-10 bg-[#0f0f0f] text-left p-4 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 min-w-[180px]">Metric</th>
                  {results.map((r) => (
                    <th key={r.domain} className="bg-[#0f0f0f] p-4 text-left min-w-[200px] border-l border-white/5">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-heading font-semibold text-white text-sm">{r.domain}</span>
                        <button data-testid={`remove-compare-${r.domain}`} onClick={() => removeDomain(r.domain)} className="text-zinc-600 hover:text-[#FF3B30]"><X size={14} /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {METRICS.map((m, idx) => (
                  <tr key={m.label} className={idx % 2 ? "bg-[#121212]" : "bg-[#0d0d0d]"}>
                    <td className="sticky left-0 z-10 p-4 text-sm text-zinc-400 border-t border-white/5" style={{ background: idx % 2 ? "#121212" : "#0d0d0d" }}>{m.label}</td>
                    {results.map((r) => {
                      const val = m.get(r);
                      return (
                        <td key={r.domain} className="p-4 border-t border-l border-white/5 font-mono text-sm">
                          {m.score ? (
                            <span className="font-bold" style={{ color: scoreColor(val) }}>{val ?? "--"}</span>
                          ) : (
                            <span className="text-zinc-200 break-all">{val || <span className="text-zinc-600">—</span>}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AIAdvisor domains={results.map((r) => r.domain)} />
        </>
      )}
    </Layout>
  );
}
